import { IModalScope } from "angular-ui-bootstrap";
import dayjs from "@gtmhub/dayjs";

import { IParameter, ICachedDynamicEntitiesMap, IParameterDynamicEntity } from "@gtmhub/parameters/models";
import { EntityService } from "@gtmhub/entities/services/EntityService";
import { IEditFiltersContext, IStringMap, IDateRangePicker, IMultiselectMap, ITimePeriod } from "../models";
import { convertDateRangeObjectToString } from "../../../util";

export interface IEditFiltersScope extends IModalScope {
  filter: IParameter;
  indicators: {
    getting: boolean;
  };
  reachedPagingEnd: boolean;
  datePickers: IStringMap;
  dateRangePickers: IDateRangePicker;
  multiselects: IMultiselectMap;
  persistedFilters: IStringMap;
  persistedFilterValueDisplayNames: IStringMap;
  selectedFilters: IStringMap;
  selectedFilterValueDisplayNames: IStringMap;
  temp: {
    selectedValue: string;
    selectedValueDisplayName: string;
    inputValue: unknown;
  };
  changeFilter(filterKey: string, value, dynamicEntityTitles: string): void;
  convertArrayToString(array: unknown[]): string;
  convertDateRangeObjectToString(dateRange: ITimePeriod): string;
  convertStringToArray(str: string): string[];
  loadDynamicEntities(filter: IParameter, search?: string, skip?: number): void;
  shouldBeChecked(entityValue: unknown): boolean;
  toggleSelection(key: string, value: string, title: string): void;
}

export class EditFiltersCtrl {
  public static $inject = ["$scope", "EntityService", "editFiltersContext"];

  constructor(private $scope: IEditFiltersScope, private entityService: EntityService, private editFiltersContext: IEditFiltersContext) {
    this.$scope.indicators = {
      getting: true,
    };
    this.$scope.changeFilter = this.changeFilter;
    this.$scope.convertArrayToString = this.convertArrayToString;
    this.$scope.convertDateRangeObjectToString = this.convertDateRangeObjectToString;
    this.$scope.convertStringToArray = this.convertStringToArray;
    this.$scope.loadDynamicEntities = this.loadDynamicEntities;
    this.$scope.shouldBeChecked = this.shouldBeChecked;
    this.$scope.toggleSelection = this.toggleSelection;
    this.$scope.$on("entityDataResponse", this.onEntityDataResponse);
    this.init();
  }

  private init = () => {
    this.$scope.multiselects = this.editFiltersContext.multiselects;
    this.$scope.datePickers = this.editFiltersContext.datePickers;
    this.$scope.selectedFilters = this.editFiltersContext.selectedFilters;
    this.$scope.persistedFilters = this.editFiltersContext.persistedFilters;
    this.$scope.persistedFilterValueDisplayNames = this.editFiltersContext.persistedFilterValueDisplayNames;
    this.$scope.selectedFilterValueDisplayNames = this.editFiltersContext.selectedFilterValueDisplayNames;
    this.$scope.filter = this.editFiltersContext.filter;

    this.$scope.temp = {
      selectedValue: this.$scope.selectedFilters[this.editFiltersContext.filter.key],
      inputValue: null,
      selectedValueDisplayName: this.$scope.selectedFilterValueDisplayNames[this.editFiltersContext.filter.key],
    };

    this.$scope.dateRangePickers = this.editFiltersContext.dateRangePickers;
    for (const [key, period] of Object.entries(this.$scope.dateRangePickers)) {
      this.$scope.dateRangePickers[key] = {
        startDate: dayjs(period.startDate).toISOString(),
        endDate: dayjs(period.endDate).toISOString(),
      };
    }
  };

  private changeFilter = (filterKey: string, value: unknown, dynamicEntityTitles: string) => {
    this.$scope.selectedFilters[filterKey] = value.toString();
    if (angular.isDate(value)) {
      this.$scope.selectedFilters[filterKey] = dayjs(value).format("YYYY-MM-DD");
    } else {
      this.$scope.selectedFilters[filterKey] = value.toString();
    }

    if (!value) {
      this.$scope.temp.selectedValue = "";
    }

    if (dynamicEntityTitles) {
      this.$scope.selectedFilterValueDisplayNames[filterKey] = dynamicEntityTitles;
    } else if (!dynamicEntityTitles && !value && this.$scope.multiselects[filterKey]) {
      this.$scope.selectedFilterValueDisplayNames[filterKey] = "";
      this.$scope.multiselects[filterKey].titles = [];
    }

    this.$scope.$close();
  };

  private convertArrayToString = (arr: unknown[]): string => {
    return arr.toString();
  };

  private convertDateRangeObjectToString = (dateRange: ITimePeriod): string => {
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      return convertDateRangeObjectToString(dateRange);
    }

    return "";
  };

  private convertStringToArray = (str?: string): string[] => {
    if (!str) {
      return [];
    }

    const converted = str.split(",");
    if (converted[0] === "") {
      converted.splice(0, 1);
    }
    return converted;
  };

  private loadDynamicEntities = (filter: IParameter, search?: string, skip?: number) => {
    this.$scope.reachedPagingEnd = false;
    if (typeof skip !== "undefined") {
      filter.skip = skip;
    } else {
      filter.skip = filter.skip + 500;
    }

    // explicitly check null else filter may have duplicate values after filter search text is deleted and 'enter' is pressed
    if (search !== null) {
      filter.dynamicEntities = [];
    }

    if (filter.entityName && filter.entityTitleField && filter.entityValueField) {
      this.$scope.indicators[filter.key] = true;

      let fieldProjection = filter.entityTitleField;

      if (filter.entityTitleField !== filter.entityValueField) {
        fieldProjection = fieldProjection + "," + filter.entityValueField;
      }

      this.entityService.requestEntityData(filter.entityName, { search, skip: filter.skip, take: 500, fields: fieldProjection, distinct: true });
    }
  };

  private onEntityDataResponse = (ev, entitiesResponse) => {
    if (entitiesResponse && entitiesResponse.entity && angular.isArray(entitiesResponse.entity)) {
      for (const filter of [this.$scope.filter]) {
        if (filter.entityName === entitiesResponse.entityName) {
          if (entitiesResponse.entity.length < 500) {
            this.$scope.reachedPagingEnd = true;
          }
          this.$scope.indicators[filter.key] = false;

          // removing duplicate values using a hash.
          const entitiesValueMap = {};
          let entityValue,
            entityTitle = "";
          // since dynamic filters are entities with 2 'value' and 'title' fields that are configurable, and only those 2 fields are fetched
          // for performance reasons, we may have the response for the right entity but with the wrong set of fields;
          // therefore we populate the filter only if both title and value fields are present in the received object.
          let isTitleAndValuePresent = false;
          for (const entity of entitiesResponse.entity) {
            // eslint-disable-next-line no-prototype-builtins
            if (entity.hasOwnProperty(filter.entityValueField) && entity.hasOwnProperty(filter.entityTitleField)) {
              entityValue = entity[filter.entityValueField].toString();
              entityTitle = entity[filter.entityTitleField];
              entitiesValueMap[entityValue] = { value: entityValue, title: entityTitle };
              // since the response may be for the same entity but with different set of fields included (fields projection)
              // we update the dynamic entities only if the response has all we need.
              isTitleAndValuePresent = true;
            }
          }

          if (isTitleAndValuePresent) {
            if (typeof filter.dynamicEntities === "undefined" || filter.dynamicEntities === null) {
              filter.dynamicEntities = [];
            }

            filter.dynamicEntitiesValueMap = entitiesValueMap;

            for (const key of Object.keys(entitiesValueMap)) {
              filter.dynamicEntities.push(entitiesValueMap[key]);
            }

            if (typeof filter.cachedDynamicEntitiesMap === "undefined" || filter.cachedDynamicEntitiesMap === null) {
              filter.cachedDynamicEntitiesMap = {};
            }

            this.populateCachedDynamicEntitiesMap(filter.cachedDynamicEntitiesMap, filter.dynamicEntities);
          }
        }
      }
    } else {
      console.warn("Invalid entity response data encountered. Event: '" + ev.name + "' Data: '" + entitiesResponse + "'");
    }
  };

  private populateCachedDynamicEntitiesMap = (cachedDynamicEntitiesMap: ICachedDynamicEntitiesMap, dynamicEntities: IParameterDynamicEntity[]) => {
    dynamicEntities.forEach((entity) => {
      cachedDynamicEntitiesMap[entity.value] = entity.title;
    });
  };

  private shouldBeChecked = (entityValue: unknown): boolean => {
    return this.$scope.multiselects[this.editFiltersContext.filter.key].selection.indexOf(entityValue.toString()) > -1;
  };

  // this method toggles the selection in the multiselect
  private toggleSelection = (key: string, value: string, title: string) => {
    const idx = this.$scope.multiselects[key].selection.indexOf(value);

    if (idx > -1) {
      this.$scope.multiselects[key].selection.splice(idx, 1);
      this.$scope.multiselects[key].titles = this.$scope.multiselects[key].titles.filter((el, index) => {
        return index !== idx;
      });
    } else {
      this.$scope.multiselects[key].selection.push(value);
      this.$scope.multiselects[key].titles.push(title || value);
    }
  };
}
